/**
 * Shared in-page replacement for window.alert / confirm / prompt. Native dialogs freeze browser automation (CDP), so all
 * UI code uses these instead. Stable hooks for automation:
 *   container  #peak-modal  (role=dialog or alertdialog, aria-modal=true, aria-labelledby/-describedby)
 *   message    #peak-modal-message
 *   input      #peak-modal-input   (prompt only)
 *   buttons    #peak-modal-ok  (text "OK")   #peak-modal-cancel  (text "Cancel", not shown for alert)
 * Esc = cancel (alert: OK), Enter = OK. Only one dialog is shown at a time; extra requests queue.
 *
 * Declarative use (no inline script, works in the closed Shadow DOM): see installPeakDialogHandlers.
 *   <a data-confirm="Text" href="...">           confirm, then follow the link
 *   <form data-confirm="Text">                   confirm, then submit
 *   <a data-form-id="f1" [data-confirm|data-prompt + data-prompt-field]>   submit hidden form f1 (after confirm/prompt)
 */
let dialogRoot: ShadowRoot | Document | null = null;
let queue: Array<() => Promise<void>> = [];
let running = false;

export function setPeakDialogRoot(root: ShadowRoot | Document) {
    dialogRoot = root;
}

function getHost(): HTMLElement {
    const r: any = dialogRoot;
    if (r && r.body) return r.body;
    if (r && r.appendChild) return r as any;
    return document.body;
}

function run(task: () => Promise<void>) {
    queue.push(task);
    if (running) return;
    running = true;
    (async () => {
        while (queue.length) {
            const t = queue.shift()!;
            try {
                await t();
            } catch (e) {
                console.error(e);
            }
        }
        running = false;
    })();
}

type Kind = "alert" | "confirm" | "prompt";

function show(kind: Kind, message: string, defaultValue: string): Promise<string | boolean | null> {
    return new Promise((resolve) => {
        run(() => new Promise<void>((done) => {
            const prevFocus = (document.activeElement as HTMLElement) || null;
            const overlay = document.createElement("div");
            overlay.id = "peak-modal-overlay";
            overlay.setAttribute("style", "position:fixed;left:0;top:0;right:0;bottom:0;z-index:2147483000;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;");
            const box = document.createElement("div");
            box.id = "peak-modal";
            box.setAttribute("role", kind === "alert" ? "alertdialog" : "dialog");
            box.setAttribute("aria-modal", "true");
            box.setAttribute("aria-labelledby", "peak-modal-message");
            box.setAttribute("aria-describedby", "peak-modal-message");
            box.setAttribute("data-peak-modal-kind", kind);
            box.setAttribute("style", "background:#fff;color:#222;max-width:480px;width:100%;border-radius:6px;padding:20px;box-shadow:0 8px 30px rgba(0,0,0,.4);font:15px/1.4 Arial,sans-serif;box-sizing:border-box;");
            const msg = document.createElement("div");
            msg.id = "peak-modal-message";
            msg.setAttribute("style", "white-space:pre-wrap;word-wrap:break-word;margin-bottom:16px;");
            msg.textContent = message;
            box.appendChild(msg);
            let input: HTMLInputElement | null = null;
            if (kind === "prompt") {
                input = document.createElement("input");
                input.id = "peak-modal-input";
                input.type = "text";
                input.value = defaultValue;
                input.setAttribute("aria-labelledby", "peak-modal-message");
                input.setAttribute("style", "width:100%;box-sizing:border-box;padding:8px;margin-bottom:16px;font-size:15px;");
                box.appendChild(input);
            }
            const row = document.createElement("div");
            row.setAttribute("style", "display:flex;justify-content:flex-end;gap:8px;");
            const btnStyle = "padding:8px 18px;font-size:15px;border-radius:4px;cursor:pointer;border:1px solid #888;";
            let cancel: HTMLButtonElement | null = null;
            if (kind !== "alert") {
                cancel = document.createElement("button");
                cancel.id = "peak-modal-cancel";
                cancel.type = "button";
                cancel.textContent = "Cancel";
                cancel.setAttribute("style", btnStyle + "background:#eee;color:#222;");
                row.appendChild(cancel);
            }
            const ok = document.createElement("button");
            ok.id = "peak-modal-ok";
            ok.type = "button";
            ok.textContent = "OK";
            ok.setAttribute("style", btnStyle + "background:#1a5fb4;color:#fff;border-color:#1a5fb4;");
            row.appendChild(ok);
            box.appendChild(row);
            overlay.appendChild(box);

            let finished = false;
            const finish = (okPressed: boolean) => {
                if (finished) return;
                finished = true;
                document.removeEventListener("keydown", onKey, true);
                overlay.remove();
                try { prevFocus && prevFocus.focus && prevFocus.focus(); } catch (e) { /* ignore */ }
                if (kind === "alert") resolve(true);
                else if (kind === "confirm") resolve(okPressed);
                else resolve(okPressed ? input!.value : null);
                done();
            };
            const onKey = (e: KeyboardEvent) => {
                if (e.key === "Escape") {
                    e.preventDefault(); e.stopPropagation();
                    finish(kind === "alert");
                } else if (e.key === "Enter" && !(e.target instanceof HTMLButtonElement && e.target === cancel)) {
                    e.preventDefault(); e.stopPropagation();
                    finish(true);
                } else if (e.key === "Tab") {
                    // keep focus inside the dialog
                    const f = Array.from(box.querySelectorAll("input,button")) as HTMLElement[];
                    const act = (box.getRootNode() as any).activeElement as HTMLElement | null;
                    const i = f.indexOf(act as HTMLElement);
                    e.preventDefault();
                    f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
                }
            };
            document.addEventListener("keydown", onKey, true);
            ok.addEventListener("click", () => finish(true));
            if (cancel) cancel.addEventListener("click", () => finish(false));
            // clicking the dark backdrop does not dismiss, to avoid accidental cancels
            getHost().appendChild(overlay);
            (input || ok).focus();
            if (input) input.select();
        }));
    });
}

export function peakAlert(message: any): Promise<void> {
    return show("alert", String(message), "").then(() => undefined);
}

export function peakConfirm(message: string): Promise<boolean> {
    return show("confirm", message, "") as Promise<boolean>;
}

/** Resolves to the entered text, or null when cancelled. */
export function peakPrompt(message: string, defaultValue: string = ""): Promise<string | null> {
    return show("prompt", message, defaultValue) as Promise<string | null>;
}

const installed = new WeakSet<object>();

/**
 * Delegated handlers (capture phase) for data-confirm / data-prompt markup. Safe to call more than once per root.
 */
export function installPeakDialogHandlers(root: ShadowRoot | Document) {
    if (installed.has(root)) return;
    installed.add(root);
    const getById = (id: string) => (root as any).getElementById(id) as HTMLFormElement | null;

    root.addEventListener("click", (ev: Event) => {
        const target = ev.target as Element | null;
        if (!target || !target.closest) return;

        const formLink = target.closest("a[data-form-id]") as HTMLAnchorElement | null;
        if (formLink) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
            const form = getById(formLink.getAttribute("data-form-id")!);
            if (!form) return;
            const promptText = formLink.getAttribute("data-prompt");
            const confirmText = formLink.getAttribute("data-confirm");
            (async () => {
                if (promptText !== null) {
                    const field = form.elements.namedItem(formLink.getAttribute("data-prompt-field")!) as HTMLInputElement;
                    const v = await peakPrompt(promptText, field.value);
                    if (v === null || v.trim() === "") return;
                    field.value = v.trim();
                } else if (confirmText) {
                    if (!(await peakConfirm(confirmText))) return;
                    form.setAttribute("data-peak-confirmed", "1");
                }
                form.requestSubmit();
            })();
            return;
        }

        const link = target.closest("a[data-confirm]") as HTMLAnchorElement | null;
        if (link) {
            if (link.getAttribute("data-peak-confirmed")) {
                link.removeAttribute("data-peak-confirmed");
                return;
            }
            ev.preventDefault();
            ev.stopImmediatePropagation();
            peakConfirm(link.getAttribute("data-confirm")!).then((ok) => {
                if (ok) {
                    link.setAttribute("data-peak-confirmed", "1");
                    link.click();
                }
            });
        }
    }, true);

    root.addEventListener("submit", (ev: Event) => {
        const form = ev.target as HTMLFormElement;
        if (!form || !form.getAttribute) return;
        const confirmText = form.getAttribute("data-confirm");
        if (!confirmText) return;
        if (form.getAttribute("data-peak-confirmed")) {
            form.removeAttribute("data-peak-confirmed");
            return;
        }
        ev.preventDefault();
        ev.stopImmediatePropagation();
        peakConfirm(confirmText).then((ok) => {
            if (ok) {
                form.setAttribute("data-peak-confirmed", "1");
                form.requestSubmit();
            }
        });
    }, true);
}
